import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IPageHeadline } from 'app/modules/shared/classes/page-title-parameters';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ILargeCardInteriorOptions } from 'app/modules/shared/interfaces/card-data';
import {
    DisplayOptions,
    IOptionalSummaryList,
} from 'app/modules/shared/interfaces/display-options';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import { IProfitabilityDataModel, IProfitabilityParams } from '../../interfaces/profitability-data';
import { ProfitabilityService } from '../../services/profitability.service';
import * as Highcharts from 'highcharts';
import { IncentivesProfitabilityChartOptions } from '../../../shared/classes/incentives-insight-profitability-chartoptions';

@Component({
    selector: 'app-incentives-insight-profitability',
    templateUrl: './incentives-insight-profitability.component.html',
    styleUrls: ['./incentives-insight-profitability.component.scss'],
})
export class IncentivesInsightProfitabilityComponent implements OnInit {
    currentLevel: Level;
    hierarchyIdentifier: string = null;
    loading: boolean = false;
    dataLoading: boolean = false;
    links: Link[] = [];
    action: NoDataActionType;
    pageLoading: boolean = true;
    displayOptions: DisplayOptions[];
    noDataType: NoDataTypes;
    pageHeadline: IPageHeadline = {
        module: 'financials',
        moduleIcon: 'fa-dollar-sign',
        pageTitle: 'Loan Profitability',
        linkArray: [
            {
                moduleLink: 'financials',
                linkTitle: 'Financials Home',
                showLink: true,
                isHome: true,
            },
            {
                moduleLink: 'financials/insights',
                linkTitle: 'Financials Insights',
                showLink: true,
            },
        ],
    };

    largeCardOptions: ILargeCardInteriorOptions;
    records: IProfitabilityDataModel[];
    topBottomCardData: IProfitLossCardData[];
    noDataForCompany: boolean;
    seriesData: any[];
    showNames: boolean;
    switchLabel: string = 'Show Names';
    profitabilityParams: IProfitabilityParams;
    extraParameters: IOptionalSummaryList[];

    constructor(
        private profitabilityService: ProfitabilityService,
        private reusableFunctions: ReusableFunctions,
        private router: Router
    ) {}

    ngOnInit() {
        this.action = NoDataActionType.pageStart;
        this.noDataType = {
            action: this.action,
            searchTerm: '',
            componentTitle: this.pageHeadline.pageTitle,
            inputSource: NoDataSourceType.largeDataCard,
        };

        this.extraParameters = [
            {
                title: 'fromDate',
                value: 'Date Range',
            },
        ];

        this.topBottomCardData = [
            {
                title: 'Top 10 by Profitability',
                data: [],
                startingNumber: 0,
            },
            {
                title: 'Bottom 10 by Profitability',
                data: [],
                startingNumber: 0,
            },
        ];

        this.currentLevel = Level.Entity;
        let hFilterJSON = this.reusableFunctions.getHierarchyFilters();
        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this.router.url);
        this.profitabilityParams = {
            fromDatePeriod: '',
            milestone: '',
            channel: '',
            purpose: '',
            loanType: '',
            roleName: '',
            hFilterJSON: hFilterJSON,
            loanNumber: null,
        };

        this.largeCardOptions = {
            action: NoDataActionType.pageStart,
            componentTitle: this.pageHeadline.pageTitle,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: false,
            accessToUrl: '',
            uniqueId: 'scatterplot',
            minHeight: 720,
        };

        this.setDisplayOptionsSelected();
        this.showNames = true;
        const { navigationLinks, hierarchy, identifier, titlePart } =
            this.profitabilityService.getRouteHistoryData();
        const hasRouteHistory = this.profitabilityService.getHasRouteHistory();
        if (navigationLinks.length && hasRouteHistory) {
            this.links = navigationLinks;
            this.changeHierarchy(hierarchy, identifier, titlePart);
        } else {
            this.getData();
        }
    }

    ngAfterViewInit() {
        this.pageLoading = false;
    }

    clickHandler() {
        let _this = this;
        return function (event) {
            let roleName = event?.point?.options?.name ?? '';
            let identifier = event?.point?.options?.value ?? '';
            let titlePart = event?.point?.properties?.titlePart ?? '';
            _this.changeHierarchy(roleName, identifier, titlePart);
        };
    }

    topBottomNameClickHandler(entity: IProfitabilityDataModel) {
        let roleName = entity.hierarchy;
        let identifier = entity.hierarchyIdentifier;
        let titlePart = entity.titlePart;
        this.changeHierarchy(roleName, identifier, titlePart);
    }

    changeHierarchy(hiearchy: string, identifier: string, titlePart: string) {
        const hasRouteHistory = this.profitabilityService.getHasRouteHistory();
        if (this.dataLoading) return;
        if (hasRouteHistory) {
            this.currentLevel = Level.LO;
        }
        if (this.currentLevel === Level.Loans) {
            this.hierarchyIdentifier = identifier;
            this.profitabilityParams.loanNumber = this.hierarchyIdentifier;
            this.profitabilityService.setLoanPrfitabilityDialogParams(
                this.profitabilityParams,
                true
            );
            this.profitabilityService.setNavigationLinks(this.links);
            this.profitabilityService.openLoanProfitabilityDialog();
        } else {
            if (!hasRouteHistory) {
                this.profitabilityService.setHierarchyParams(hiearchy, identifier, titlePart);
                this.links.push({
                    roleName: this.profitabilityParams.roleName,
                    hierarchyIdentifier: this.hierarchyIdentifier,
                    hierarchyName: hiearchy,
                    level: this.currentLevel,
                    titlePart: titlePart,
                });
            }

            this.currentLevel++;
            this.hierarchyIdentifier = identifier;
            this.getData();
        }
    }

    resetValues() {
        this.links = [];
        this.hierarchyIdentifier = null;
        this.currentLevel = Level.Entity;
    }

    goBack() {
        this.profitabilityService.setHasRouteHistory(false);
        let _this = this;
        return function () {
            if (_this.dataLoading) return;
            if (_this.links && _this.links.length > 0) {
                let nextLink: Link = _this.links.pop();
                _this.profitabilityParams.roleName = nextLink.roleName;
                _this.hierarchyIdentifier = nextLink.hierarchyIdentifier;
                _this.currentLevel = nextLink.level;
                _this.getData();
            }
        };
    }

    getData() {
        this.dataLoading = true;
        this.largeCardOptions.isDataReady = false;
        this.largeCardOptions.hasData = false;
        this.largeCardOptions.chart = null;
        this.largeCardOptions = Object.assign({}, this.largeCardOptions);
        let tempParams = Object.assign({}, this.profitabilityParams);
        if (this.profitabilityParams.roleName === 'Employee' && this.currentLevel !== Level.Loans) {
            this.currentLevel = Level.LO;
            tempParams.roleName = null;
        }
        if (this.currentLevel === Level.LO) {
            tempParams.roleIdentifier = this.hierarchyIdentifier;
            this.profitabilityService.getGLLOInsightsCard(tempParams).subscribe(
                (data) => {
                    this.setData(data);
                },
                (error) => {
                    this.action = NoDataActionType.error;
                    this.setNoData();
                }
            );
        } else if (this.currentLevel == Level.Entity) {
            tempParams.roleIdentifier = this.hierarchyIdentifier;
            this.profitabilityService.getGLRoleInsightsCard(tempParams).subscribe(
                (data) => {
                    this.setData(data);
                },
                (error) => {
                    this.action = NoDataActionType.error;
                    this.setNoData();
                }
            );
        } else {
            tempParams.employeeIdentifier = this.hierarchyIdentifier;
            this.profitabilityService.getGLLoanInsightsCard(tempParams).subscribe(
                (data) => {
                    this.setData(data);
                },
                (error) => {
                    this.action = NoDataActionType.error;
                    this.setNoData();
                }
            );
        }
    }

    setData(data: any) {
        this.loading = false;
        if (data && data.length > 0) {
            this.noDataForCompany = false;
            this.records = data;
            this.setDataForCards();
        } else {
            this.setNoData();
            this.noDataForCompany = true;
            this.drawChart();
        }
        this.dataLoading = false;
    }

    setNoData() {
        this.noDataForCompany = true;
        this.loading = false;
        this.dataLoading = false;
        this.noDataType.action = this.action;
        this.largeCardOptions.isDataReady = true;
        this.largeCardOptions.hasData = false;
        this.largeCardOptions.action = this.action;
        this.largeCardOptions = Object.assign({}, this.largeCardOptions);
    }

    setDisplayOptionsSelected() {
        let fromDate = this.displayOptions.find((item) => item.optionKey === 'dateRangeFinancials');
        this.profitabilityParams.fromDatePeriod = fromDate?.selectedOptions ?? '';
        let channelInfo = this.displayOptions.find((item) => item.optionKey === 'channel');
        this.profitabilityParams.channel = channelInfo?.selectedOptions ?? '';
        let loanTypeInfo = this.displayOptions.find((item) => item.optionKey === 'loanCategory');
        this.profitabilityParams.loanType = loanTypeInfo?.selectedOptions ?? '';
        let purposeInfo = this.displayOptions.find((item) => item.optionKey === 'loanPurpose');
        this.profitabilityParams.purpose = purposeInfo?.selectedOptions ?? '';
        let groupByRolesInfo = this.displayOptions.find(
            (item) => item.optionKey === 'groupByRoles_topSelect'
        );
        this.profitabilityParams.roleName = groupByRolesInfo?.selectedOptions ?? '';
        let viewByInfo = this.displayOptions.find((item) => item.optionKey === 'viewBy_Sold_to');
        this.profitabilityParams.milestone = viewByInfo?.selectedOptions ?? '';
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.displayOptions = event;
        this.resetValues();
        this.action = NoDataActionType.filter;
        this.setDisplayOptionsSelected();
        this.getData();
    }

    applyNewFilters(event: string) {
        this.resetValues();
        this.action = NoDataActionType.filter;
        this.profitabilityParams.hFilterJSON = event;
        this.getData();
    }

    drawChart(): void {
        let profitabilityCharts = new IncentivesProfitabilityChartOptions(this.reusableFunctions);
        const isLoan = this.links.length > 1 ? true : false;
        const xTitle = isLoan ? 'Loan Amount ($)' : 'Volume ($)';
        setTimeout(() => {
            if (!this.loading) {
                this.largeCardOptions.isDataReady = true;
                this.largeCardOptions.hasData =
                    this.seriesData?.length > 0 && !this.noDataForCompany ? true : false;
            }

            let title = this.setTitle();
            if (this.seriesData?.length === 1) {
                this.seriesData[0].y = Number(this.seriesData[0].y.toFixed(3));
            }
            this.largeCardOptions.chart = new Highcharts.Chart(
                'scatterplot',
                profitabilityCharts.createProfitabilityScatterPlot(
                    title,
                    xTitle,
                    'BPS',
                    this.seriesData,
                    '#41D6C3',
                    this.showNames,
                    this.clickHandler(),
                    isLoan,
                    this
                )
            );
            this.largeCardOptions = Object.assign({}, this.largeCardOptions);
        });
    }

    setTitle(): string {
        let title = 'Loan Profitability by ';
        if (this.links.length === 1) {
            if (this.profitabilityParams.roleName === 'Employee') {
                return title + this.links[0].titlePart;
            }
            return (
                title +
                `LOs at ${this.profitabilityParams.roleName}
                            ${this.links[0].hierarchyName}`
            );
        } else if (this.links.length > 1) {
            let titlePart = '';
            this.links.forEach((link, i) => {
                if (i > 0) {
                    titlePart += `${this.links[i].titlePart} at
                                    ${this.profitabilityParams.roleName}
                                    ${this.links[i - 1].hierarchyName}`;
                }
            });
            return title + titlePart;
        } else {
            return title + `${this.profitabilityParams.roleName}`;
        }
    }

    mouseEnter(employee: IProfitabilityDataModel) {
        if (this.pageLoading) return;
        let employeeName = employee.hierarchy;
        let seriesData = this.largeCardOptions?.chart?.series[0]?.data ?? [];
        employeeName = employee.hierarchy;
        seriesData.forEach((point) => {
            if (point.name === employeeName) {
                point.setState('hover');
                this.largeCardOptions.chart?.tooltip?.refresh(point);
            }
        });
    }

    mouseLeave(employee: IProfitabilityDataModel) {
        let employeeName = employee.hierarchy;
        let seriesData = this.largeCardOptions?.chart?.series[0]?.data ?? [];
        employeeName = employee.hierarchy;

        seriesData.forEach((point) => {
            if (point.name === employeeName) {
                point.setState('normal');
            }
        });
        this.largeCardOptions.chart?.tooltip?.hide(100);
    }

    setDataForCards() {
        this.seriesData = [];
        this.records.map((record) => {
            if (this.currentLevel === Level.LO) {
                let nameInf = this.abbreviateNameToLastPlustFirstInitial(record.hierarchy);
                record.hierarchy = nameInf.lastFirstInitial;
                record.titlePart = nameInf.firstLastName;
            } else if (this.currentLevel === Level.Loans) {
                record.hierarchy = record.hierarchyIdentifier;
                record.titlePart = record.hierarchy;
            } else {
                record.hierarchy = record.hierarchy.split(' - ').pop();
                record.titlePart = record.hierarchy;
            }
            let isLoss = record.revenue < 0;
            record.revenueFormatted =
                '$' + Math.abs(Math.round(record.revenue))?.toLocaleString() ?? '';

            if (isLoss) {
                record.revenueFormatted = '(' + record.revenueFormatted + ')';
            }

            this.seriesData.push({
                name: record.hierarchy,
                value: record.hierarchyIdentifier,
                x: record.volume,
                y: record.bps,
                properties: {
                    revenue: record.revenueFormatted,
                    bps: record.bps.toFixed(3),
                    titlePart: record.titlePart,
                },
            });
        });
        this.drawChart();
        this.setTopAndBottomPerformers();
    }

    setTopAndBottomPerformers() {
        let totalLength = this.records.length;
        let topChunkSize = 10;
        let bottomChunkSize = 10;

        if (totalLength <= 20) {
            topChunkSize = Math.round(totalLength / 2);
            bottomChunkSize = totalLength - topChunkSize;
        }

        let bottom10Top = totalLength - bottomChunkSize;
        let sortedData = this.records.sort(compareBPS);

        let topCard = this.topBottomCardData[0];
        topCard.data = sortedData.slice(0, topChunkSize);
        topCard.startingNumber = 1;

        let bottomCard = this.topBottomCardData[1];
        bottomCard.data = sortedData.slice(bottom10Top, totalLength);
        bottomCard.startingNumber = bottom10Top + 1;
    }

    abbreviateNameToLastPlustFirstInitial(fullName: string) {
        let nameParts = fullName.split(', ');
        let firstName = nameParts.length > 1 ? nameParts[1] : '';
        let lastName = nameParts[0];
        return {
            lastFirstInitial: lastName + ', ' + firstName.charAt(0) ?? '',
            firstLastName: firstName + ' ' + lastName,
        };
    }

    setToggleLabel() {
        this.switchLabel = 'Show Names';
    }

    toggleDataLabels(): void {
        this.setToggleLabel();
        this.showNames = !this.showNames;
        this.drawChart();
    }
}

function compareBPS(a, b) {
    if (a.bps > b.bps) {
        return -1;
    }
    if (a.bps < b.bps) {
        return 1;
    } else if (a.volume > b.volume) {
        return -1;
    } else if (a.volume < b.volume) {
        return 1;
    }
    return 0;
}

enum Level {
    Entity = 1,
    LO = 2,
    Loans = 3,
}

interface Link {
    roleName: string;
    hierarchyIdentifier: string;
    hierarchyName: string;
    level: Level;
    titlePart: string;
}

interface IProfitLossCardData {
    title: string;
    startingNumber: number;
    data: IProfitabilityDataModel[];
}
